import "azure-devops-ui/CommonImports";
import "azure-devops-ui/Core/core.css";
import "./Ansi.css";
import "./LogViewer.css";
import * as Resources from '../../Resources';
import { LogParser } from '../../Utilities/LogParser';
import { getText, Parser, TimestampRegex } from '../../Utilities/Parser';
import * as React from "react";
import { ObservableValue } from "azure-devops-ui/Core/Observable";
import { Button } from "azure-devops-ui/Button";
import { Icon } from "azure-devops-ui/Icon";
import { Intersection } from "azure-devops-ui/Intersection";
import { Measure } from "azure-devops-ui/Measure";
import { Observer } from "azure-devops-ui/Observer";
import { Spinner, SpinnerSize } from "azure-devops-ui/Spinner";
import { Status, Statuses, StatusSize } from "azure-devops-ui/Status";
import { Tooltip } from "azure-devops-ui/TooltipEx";
import { FixedHeightTree } from "azure-devops-ui/TreeEx";
import { css } from "azure-devops-ui/Util";
import { TreeItemProvider } from "azure-devops-ui/Utilities/TreeItemProvider";
import { LineTransform } from "./LineTransform";
const dataLine = "data-line";
const dataSection = "data-sec";
const dataLineSection = "data-lsec";
const mainContentClass = "reader-main-content";
const Line = (props) => {
    // note: we have a test case in AnsiParser.test.ts that tests for XSS
    const copied = props.copy && props.copy.key === props.sectionKey && props.copy.line === props.line;
    const Element = props.onClick ? Button : "span";
    return (React.createElement(Element, Object.assign({ key: props.index, className: css("line-area flex-center flex-row flex-grow justify-start", props.className, props.onClick && "cursor-pointer"), role: "group" }, (props.onClick
        ? {
            onClick: props.onClick
        }
        : {})),
        React.createElement("span", Object.assign({ className: "flex-noshrink flex-self-start line", "data-lsec": props.sectionKey, "data-line": props.line, role: "treeitem" }, (props.isSnap
            ? {
                "data-snap": "1"
            }
            : {}), (props.onCopyLink
            ? {
                onClick: props.onCopyLink
            }
            : {}))),
        props.content && React.createElement("span", { className: "content" }, props.content),
        props.onCopyLink && (React.createElement(Tooltip, { text: copied ? Resources.Copied : Resources.CopyLink },
            React.createElement("span", { className: "link", "data-lsec": props.sectionKey, "data-line": props.line, onClick: props.onCopyLink },
                React.createElement(Icon, { iconName: "Link", className: "fontSizeM" }))))));
};
/**
 * Hosts log component, note that this is made to work inside a panel, some calculations might have to check to work else where
 * This virtualizes lines using the List component.
 */
export class LogViewer extends React.Component {
    constructor(props) {
        super(props);
        this._subParser = new Parser();
        this._container = React.createRef();
        // Needed to support sticky horizontal section header.
        this._viewerWidth = new ObservableValue(0);
        // The largest row may be virtualized, so we need to keep track of the largest row width to set the horizontal scrollbar.
        this._largestRowWidth = new ObservableValue(0);
        // line to scroll controlled by find
        this._findLine = new ObservableValue(undefined);
        this._find = new Find();
        this._findText = "";
        this._scrollPending = false;
        this._autoScroll = true;
        // The maximum height of the table when virtualized. Browsers have issues
        // rendering elements that are too large and when the LogViewer contains thousands
        // of elements, the list renders very large spacer elements to correctly
        // position the scroll bar. The large spacer elements cause rendering issues
        // across browsers. To bypass this, we need to limit how large the list can
        // grow to. By default this size is 8,500,000px based of Mozilla Firefox restrictions.
        // Other browsers based on Chromium like Chrome, Opera, Edge etc can
        // render up to 30,000,000px
        this._maxHeight = 8500000;
        // observables for sections
        this._showLog = {};
        this._data = {};
        this._sections = {};
        this._selectionChanged = {};
        this._copyChanged = {};
        // content pending tracking for sections
        this._pending = {};
        this._sectionTreeRef = {};
        /**
         * Scrolls to the very top of the view, handles virtualizations so that we are at the true top
         */
        this.toHome = () => {
            this._noScroll();
            this._jump(true);
        };
        /**
         * Scrolls to the very end of the view, handles virtualizations so that we are at the true end
         */
        this.toEnd = () => {
            this._jump();
        };
        this._containerKeyDown = (event) => {
            // override select all to only select the text from the current focused section if exists
            if (this._toSelectAll && (event.ctrlKey || event.metaKey) && event.key === "a") {
                const currentSection = document.querySelector(`[${dataSection}="${this._toSelectAll}"]`);
                if (currentSection) {
                    const selection = window.getSelection();
                    const range = document.createRange();
                    range.selectNodeContents(currentSection);
                    selection.removeAllRanges();
                    selection.addRange(range);
                    event.preventDefault();
                }
            }
        };
        this._getTreeRef = (sectionKey) => {
            if (!this._sectionTreeRef[sectionKey]) {
                this._sectionTreeRef[sectionKey] = React.createRef();
            }
            return this._sectionTreeRef[sectionKey];
        };
        this._bindKeys = (event) => {
            const target = event.target;
            // eg: pressing Home inside search box
            if (target && target.tagName === "INPUT") {
                return;
            }
            if (event.key === "Home") {
                event.preventDefault();
                this.toHome();
            }
            else if (event.key === "End") {
                event.preventDefault();
                this.toEnd();
            }
        };
        this._onMeasureContainer = (newWidth, newHeight) => {
            if (this._viewerWidth.value !== newWidth) {
                this._viewerWidth.value = newWidth;
                if (this._viewerWidth.value > this._largestRowWidth.value) {
                    this._largestRowWidth.value = this._viewerWidth.value;
                }
            }
        };
        this._onSectionsChanged = (newData) => {
            (newData.removedItems || []).forEach((x) => {
                delete this._showLog[x.key];
                delete this._data[x.key];
                delete this._selectionChanged[x.key];
                delete this._copyChanged[x.key];
                delete this._sections[x.key];
            });
            const newItems = newData.addedItems || [];
            const updatedItems = newData.changedItems || [];
            // initialize new items and propogate updates to existing items
            this._initializeSections(newItems.concat(updatedItems), true);
            // update observable state if there are any new items, else update to existing items will be taken care by _initializeSections
            return newItems.length > 0;
        };
        this._findFromIndex = (index, lines) => {
            if (lines && lines.length > 0) {
                for (let i = 0; i < lines.length; i++) {
                    let lineIndex = lines[i].data.nodes[0].lineIndex;
                    if (lineIndex === index) {
                        return lines[i];
                    }
                    // the index is in the children if it is the last item or the next node is too big.
                    else if (!lines[i + 1] || lines[i + 1].data.nodes[0].lineIndex > index) {
                        return this._findFromIndex(index, lines[i].childItems);
                    }
                }
            }
            return undefined;
        };
        this._reFind = () => {
            if (this._findText) {
                requestAnimationFrame(() => {
                    this._find.find(this._findText, this._getFindProps());
                });
            }
        };
        this._getFindContent = (key) => {
            if (this._data[key]) {
                return this._data[key].value.content;
            }
            return "";
        };
        this._onFind = (result) => {
            if (result && result.lines.length > 0) {
                const resultLine = result.lines[result.toScrollIndex];
                const sectionData = this._data[resultLine.key] && this._data[resultLine.key].value;
                if (sectionData) {
                    this._findLine.value = {
                        key: resultLine.key,
                        line: resultLine.line
                    };
                    this._scrollPending = true;
                    sectionData.findResult = result;
                    this._scrollToLine();
                }
            }
            else {
                this._findLine.value = undefined;
                // get all expanded sections and update data
                Object.keys(this._showLog).forEach((x) => {
                    let newData = Object.assign({}, this._data[x].value);
                    newData.findResult = undefined;
                    this._data[x].value = newData;
                });
            }
        };
        this._onScroll = () => {
            const scrollElement = this._container.current;
            if (this._scrollPending) {
                return;
            }
            // If the user has scrolled to the current section that is auto-scrolling, enable autoscroll.
            let lastLine = false;
            if (this._autoScrollLine) {
                const tree = this._getTreeRef(this._autoScrollLine.key).current;
                const stats = tree && tree.getStats();
                lastLine = this._autoScrollLine.line === (stats && stats.lastMaterialized + 1);
            }
            // If the user has scrolled within 25px of the end of the scroll element, trigger autoscroll. We may want to fine tune this in the future.
            this._autoScroll =
                lastLine ||
                    (!!this._sections && !!scrollElement && scrollElement.scrollHeight - scrollElement.offsetHeight - scrollElement.scrollTop < 25);
        };
        this._onSnapLineClick = (event) => {
            if (event) {
                const liElement = event.currentTarget;
                if (liElement) {
                    event.stopPropagation();
                    const element = liElement.querySelector(".link");
                    if (element) {
                        const sectionKey = element.getAttribute(dataLineSection) || "";
                        this._sections[sectionKey].value.isExpanded = false;
                        const number = parseInt(element.getAttribute(dataLine) || "");
                        this.select(sectionKey, number);
                    }
                }
            }
        };
        this._copyLink = (event) => {
            var _a;
            if (event) {
                const linkElement = (_a = event.target) === null || _a === void 0 ? void 0 : _a.closest('.link');
                if (linkElement) {
                    event.stopPropagation();
                    const sectionKey = linkElement.getAttribute(dataLineSection) || "";
                    const number = parseInt(linkElement.getAttribute(dataLine) || "");
                    const section = this.props.sections.value.find((x) => x.key === sectionKey);
                    if (section && section.getLineLink) {
                        const link = section.getLineLink(section, number);
                        // copy link
                        // IE11 Compatible
                        if (window.clipboardData) {
                            window.clipboardData.setData("text", link);
                        }
                        const listener = (e) => {
                            if (e.clipboardData) {
                                e.clipboardData.setData("text", link);
                            }
                            e.preventDefault();
                        };
                        document.addEventListener("copy", listener);
                        const result = document.execCommand("copy");
                        document.removeEventListener("copy", listener);
                        if (result) {
                            this._copy = {
                                key: sectionKey,
                                line: number
                            };
                            this._copyChanged[sectionKey].value = !this._copyChanged[sectionKey].value;
                            window.history.replaceState(window.history.state, document.title, link);
                        }
                    }
                }
            }
        };
        this._parser = new LogParser();
        this.state = {
            findActive: false
        };
        this._initializeSections(this.props.sections.value);
    }
    render() {
        return (React.createElement(Observer, { sections: { observableValue: this.props.sections, filter: this._onSectionsChanged }, find: this._find.result }, (props) => {
            const sections = props.sections;
            return (React.createElement(Intersection, null,
                React.createElement("div", { "aria-label": Resources.LogSection, className: `pl-reader fontSize ${this.props.className}`, onScroll: this._onScroll, ref: this._container, onKeyDown: this._containerKeyDown },
                    React.createElement(Measure, { onMeasure: this._onMeasureContainer },
                        React.createElement("div", { className: `${mainContentClass} flex-grow` },
                            React.createElement("ol", null, sections.map((sectionFromObservable) => {
                                const sectionKey = sectionFromObservable.key;
                                return (React.createElement("li", { key: sectionKey, "data-sec": sectionKey },
                                    React.createElement(Observer, { findLine: this._find.result, showLog: this._showLog[sectionKey], data: this._data[sectionKey], section: this._sections[sectionKey], largestRowWidth: this._largestRowWidth, viewerWidth: this._viewerWidth }, (sectionProps) => {
                                        // constructing data that's needed to render lines
                                        const s = sectionProps.section;
                                        const lines = sectionProps.data.lines || [];
                                        const linesToRender = sectionProps.data.lines || [];
                                        const showLog = sectionProps.showLog && linesToRender.length > 0;
                                        const showSpinner = !!(this._showLog[sectionKey] &&
                                            this._showLog[sectionKey].value &&
                                            linesToRender.length == 0);
                                        const hasContent = s.hasData || lines.length > 0;
                                        const noStatusFaint = s.statusProps && s.statusProps === Statuses.Failed;
                                        // constructing data that's needed to parse find results
                                        let parsedFindResult = undefined;
                                        const dataFindResult = sectionProps.findLine;
                                        if (dataFindResult) {
                                            const marker = dataFindResult.markers[sectionKey];
                                            if (marker) {
                                                parsedFindResult = Object.assign({}, dataFindResult);
                                                // send slice of lines that belong to this section
                                                parsedFindResult.lines = dataFindResult.lines;
                                                // send selected line
                                                const selectedLine = dataFindResult.lines[dataFindResult.toScrollIndex];
                                                if (selectedLine && selectedLine.key === sectionKey) {
                                                    parsedFindResult.selectedLine = selectedLine.line;
                                                }
                                            }
                                        }
                                        const controlId = sectionKey + "-log";
                                        const rowWidth = sectionProps.largestRowWidth <= sectionProps.viewerWidth
                                            ? "100%"
                                            : `${sectionProps.largestRowWidth}px`;
                                        // we can't use flex here since we need position:sticky to work, so we calculate the width of the section header.
                                        let buttonWidth = sectionProps.viewerWidth -
                                            (24 + 1) * 2; /* 24 padding - 1px border on each side */
                                        return (React.createElement("div", { className: css("flex-column content-area", s.getLineLink && "linkable", s.className), style: { width: rowWidth } },
                                            React.createElement("div", { className: "flex-row sec-sticky" },
                                                React.createElement(Button, Object.assign({ className: css("flex flex-row sec-area", hasContent && "cursor-pointer", showLog && "expand", s.hidden && "hide"), ariaControls: controlId, disabled: !hasContent, ariaExpanded: showLog }, (hasContent
                                                    ? { onClick: (e) => this._onToggleLog(sectionKey) }
                                                    : {})),
                                                    React.createElement("div", { className: "status-area flex flex-grow", style: { width: buttonWidth + "px" } },
                                                        React.createElement("div", { className: "flex-grow flex-self-center flex flex-row sec-content" },
                                                            React.createElement(SecStatus, { iconProps: s.iconProps, statusProps: s.statusProps, spinner: showSpinner, noStatusFaint: noStatusFaint }),
                                                            React.createElement("span", { className: "title text-ellipsis body-m" }, s.title)),
                                                        s.getFarRight && (React.createElement("div", { className: "far-right body-s flex-self-center" }, s.getFarRight(s))))),
                                                React.createElement("div", { className: "flex-row flex-grow" })),
                                            showSpinner ? (React.createElement(Spinner, { size: SpinnerSize.large, label: Resources.SpinnerLabel })) : (React.createElement(FixedHeightTree, { id: controlId, className: css(!showLog && "hide"), focuszoneProps: {
                                                    activateOnEnter: !!s.getLineLink
                                                }, itemProvider: showLog
                                                    ? sectionProps.data.treeProvider
                                                    : new TreeItemProvider([]), maxHeight: this._maxHeight, onToggle: (event, treeItem) => {
                                                    if (treeItem.underlyingItem.childItems &&
                                                        sectionProps.data.treeProvider &&
                                                        !event.defaultPrevented) {
                                                        sectionProps.data.treeProvider.toggle(treeItem.underlyingItem);
                                                        event.preventDefault();
                                                    }
                                                }, pageSize: 60, renderLoadingRow: (rowIndex, rowDetails) => {
                                                    const asyncInfo = sectionProps.section.asyncInfo;
                                                    if (asyncInfo) {
                                                        asyncInfo.getMore(asyncInfo.payload);
                                                        return (React.createElement("div", { className: "flex-row flex-grow" },
                                                            React.createElement("div", { className: "shimmer shimmer-line log-async-loading" })));
                                                    }
                                                    return React.createElement("div", null);
                                                }, renderRow: (index, item, details) => {
                                                    return (React.createElement(Observer, { findResult: this._find.result, x: this._copyChanged[sectionKey] }, (rowProps) => {
                                                        const lineNumber = item.underlyingItem.data.lineNumber;
                                                        const isSelected = this._selection &&
                                                            this._selection.key === sectionKey;
                                                        const isLineSelected = isSelected &&
                                                            this._selection.lineToScroll ===
                                                                lineNumber;
                                                        const isCopiedLine = this._copy && this._copy.line === lineNumber;
                                                        const extraClassName = isLineSelected || isCopiedLine ? "highlight" : "";
                                                        const dataFindResult = rowProps.findResult;
                                                        let lineParsedResult = undefined;
                                                        if (dataFindResult) {
                                                            const marker = dataFindResult.markers[sectionKey];
                                                            if (marker) {
                                                                lineParsedResult = Object.assign({}, dataFindResult);
                                                                // send slice of lines that belong to this section
                                                                lineParsedResult.lines = dataFindResult.lines;
                                                                // send selected line
                                                                const selectedLine = dataFindResult.lines[dataFindResult.toScrollIndex];
                                                                if (selectedLine &&
                                                                    selectedLine.key === sectionKey) {
                                                                    lineParsedResult.selectedLine =
                                                                        selectedLine.line;
                                                                }
                                                            }
                                                        }
                                                        const transformedLine = (React.createElement(LineTransform, { expanded: item.underlyingItem.expanded, section: sectionKey, line: item.underlyingItem.data, pureHTML: s.showHtml, subParser: this._subParser, raw: sectionProps.data.content, lineNumber: lineNumber, findResult: lineParsedResult, contentId: `rowContent-${index}` }));
                                                        return (React.createElement("div", { className: css("line-row flex-row flex-grow", item.underlyingItem.childItems &&
                                                                "group-item") },
                                                            React.createElement(Line, { index: lineNumber, sectionKey: sectionKey, className: extraClassName, line: lineNumber, content: transformedLine, onCopyLink: s.getLineLink && this._copyLink, copy: this._copy })));
                                                    }));
                                                }, rowHeight: 20, ref: sectionProps.data.treeRef })),
                                            !showLog && s.snaps && s.snaps.length > 0 && (React.createElement("div", { className: "lines-content" }, s.snaps.map((snap, index) => {
                                                const lines = this._parser.parse(snap.content);
                                                // snap content is supposed to have only one line
                                                const lineData = lines[0];
                                                const transformed = (React.createElement(LineTransform, { section: sectionKey, line: lineData, subParser: this._subParser, raw: snap.content, lineNumber: snap.line, findResult: parsedFindResult }));
                                                return (transformed && (React.createElement(Line, { index: index, sectionKey: sectionKey, key: sectionKey + index, className: "snap", isSnap: true, line: snap.line, content: transformed, onClick: this._onSnapLineClick, onCopyLink: s.getLineLink && this._copyLink, copy: this._copy })));
                                            })))));
                                    })));
                            })))))));
        }));
    }
    componentDidMount() {
        const sectionToGet = this._selection && this._selection.key;
        if (sectionToGet) {
            this._setContent(sectionToGet);
        }
        if (this._container.current) {
            this._container.current.focus();
        }
        if (this._container.current && typeof ResizeObserver !== "undefined") {
            this._resizeObserver = new ResizeObserver(([entry]) => {
                this._onMeasureContainer(entry.contentRect.width, entry.contentRect.height);
            });
            this._resizeObserver.observe(this._container.current);
        }
        this._find.result.subscribe(this._onFind);
    }
    componentWillUnmount() {
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
        }
        this._unregisterScrollable();
        this._find.result.unsubscribe(this._onFind);
    }
    /**
     * This can handle situations that could break user expectations with scrolling
     * Eg: Pressing "End" should go the true end
     * @param element Elements that's scrollable
     */
    initializeScrollable(element) {
        this._scrollable = element;
        this._scrollable.addEventListener("keydown", this._bindKeys);
    }
    /**
     * Selects a section/line
     * @param key section to select
     * @param lineNumber linenumber to scroll to in the specified section
     */
    select(key, lineNumber) {
        if (!this._showLog[key]) {
            // bad selection
            return;
        }
        if (lineNumber) {
            this._selection = {
                key: key,
                lineToScroll: lineNumber
            };
            this._toLine = {
                key: key,
                line: lineNumber
            };
            this._scrollPending = true;
        }
        else {
            // if linenumber is not given, "unselect" the existing line selection if it exists
            this._selection = undefined;
        }
        // After we initialize lines to scroll as needed, make sure that we show this section, note that we don't collapse once we expanded, so there's no way to "unselect" an expanded section/task
        this._setContent(key);
        // notify observer to re-render that section
        this._selectionChanged[key].value = !this._selectionChanged[key].value;
        this._toSelectAll = key;
    }
    /**
     * Updates the content in the section by force calling getData() to get new content and auto expanding the section if the user didn't explictly interacted with the section
     * If this section already sent hasContent as well, there's no need for the section to update that, we handle the case where we have data even if hasContent is false
     * @param key section to update content for
     */
    updateContent(key) {
        // updates the content for section at key only if there's no existing state/ user didn't interact with it yet
        const existingValue = this._showLog[key] && this._showLog[key].value;
        if (existingValue === undefined || existingValue === true) {
            this._setContent(key, true);
        }
    }
    /**
     * Brings focus to the reader
     */
    focus() {
        this._container.current && this._container.current.focus();
    }
    /**
     * Finds the supplied text with in expanded content
     */
    find(text) {
        this._noScroll();
        this._findText = text;
        this._find.find(this._findText, this._getFindProps());
    }
    /**
     * When find is active, moves to the next match
     */
    findNext() {
        if (this._findText) {
            this._find.down();
        }
    }
    /**
     * Clears find
     */
    clearFind() {
        if (this._find.result) {
            this._find.result.value = undefined;
        }
    }
    /**
     * When find is active, moves to the previous match
     */
    findPrev() {
        if (this._findText) {
            this._find.up();
        }
    }
    /**
     * Gets find result observable, consumers can subscribe to this to listen to result changes
     */
    getFindResult() {
        return this._find.result;
    }
    _noScroll() {
        this._autoScroll = false;
        delete this._autoScrollLine;
    }
    _getTreeItemsFromLines(lines) {
        let group = {};
        let groupIndex = -1;
        let treeItems = [];
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const node = line.nodes[0];
            if (line.nodes.some((n) => !!n.isGroup)) {
                if (groupIndex != -1) {
                    // add previous group
                    treeItems.push(group[groupIndex]);
                    groupIndex = -1;
                }
                groupIndex = line.nodes[0].lineIndex;
                group[groupIndex] = { data: line };
            }
            else if (node.group && group[node.group.lineIndex]) {
                const groupTreeItem = group[node.group.lineIndex];
                if (groupTreeItem.childItems) {
                    groupTreeItem.childItems.push({ data: line });
                }
                else {
                    groupTreeItem.childItems = [{ data: line }];
                }
            }
            else {
                if (groupIndex != -1) {
                    // add previous group
                    treeItems.push(group[groupIndex]);
                    groupIndex = -1;
                }
                treeItems.push({ data: line });
            }
        }
        if (groupIndex != -1) {
            treeItems.push(group[groupIndex]);
        }
        return treeItems;
    }
    _jump(top = false) {
        const allSections = this.props.sections.value || [];
        const section = top ? allSections[0] : allSections[allSections.length - 1];
        this._goTo(section, top);
    }
    /**
     * Scrolls to the section specified, handles virtualization
     * @param section section to focus on
     * @param top whether to focus on the top or bottom of the section if there's only one
     */
    _goTo(section, top) {
        if (this._container.current && section) {
            // ignore any intersections
            let element = this._container.current.querySelector(`[${dataSection}="${section.key}"]`);
            const allSections = this.props.sections.value || [];
            if (element) {
                // scroll to section if there are more than 1
                allSections.length > 1 &&
                    element.scrollIntoView({
                        behavior: "auto",
                        block: "center"
                    });
                allSections.length == 1 && element.scrollIntoView(top);
            }
        }
    }
    _unregisterScrollable() {
        if (this._scrollable) {
            this._scrollable.removeEventListener("keydown", this._bindKeys);
        }
    }
    _initializeSections(sections, isUpdate = false) {
        const selectedSection = this._selection ? this._selection.key : null;
        sections.forEach((s) => {
            if (this._sections[s.key]) {
                // propogate updates for existing item
                this._sections[s.key].value = s;
                if (isUpdate) {
                    // force update content if this is already visible
                    if (this._showLog[s.key].value) {
                        this._setContent(s.key, true);
                    }
                    return;
                }
            }
            else {
                this._sections[s.key] = new ObservableValue(s);
            }
            const value = selectedSection === s.key || s.isExpanded ? true : undefined;
            if (this._showLog[s.key]) {
                this._showLog[s.key].value = value;
            }
            else {
                this._showLog[s.key] = new ObservableValue(value);
            }
            if (this._data[s.key]) {
                this._data[s.key].value = {};
            }
            else {
                this._data[s.key] = new ObservableValue({});
            }
            if (this._selectionChanged[s.key]) {
                this._selectionChanged[s.key].value = false;
            }
            else {
                this._selectionChanged[s.key] = new ObservableValue(false);
            }
            if (this._copyChanged[s.key]) {
                this._copyChanged[s.key].value = false;
            }
            else {
                this._copyChanged[s.key] = new ObservableValue(false);
            }
            if (s.isExpanded) {
                this._setContent(s.key);
            }
        });
    }
    _scroll(section, lineNumber, placement = "center", done) {
        if (section && section.treeItems && section.treeRef && section.treeRef.current) {
            if (this.props.isGroupedLogTreeScrollIndexFixEnabled) {
                const treeItem = this._findTreeItemByLineNumber(lineNumber, section.treeItems);
                if (treeItem && section.treeProvider) {
                    section.treeProvider.expand(treeItem, true);
                    requestAnimationFrame(() => {
                        const index = section.treeProvider.value.findIndex(item => item.underlyingItem === treeItem);
                        if (index >= 0 && section.treeRef && section.treeRef.current) {
                            section.treeRef.current.scrollIntoView(index, { block: placement });
                        }
                        requestAnimationFrame(() => {
                            done && done();
                        });
                    });
                    return;
                }
            }
            let treeItem;
            let index;
            if (this.props.scrollToLogLineWhenGroupLogsArePresent) {
                let [foundTreeItem, foundIndex] = this._findTreeItemAndLineIndex(lineNumber, section.treeItems);
                treeItem = foundTreeItem;
                index = foundIndex - 1; // we start from line 1
            }
            else {
                index = lineNumber - 1;
                treeItem = this._findFromIndex(index, section.treeItems);
            }
            treeItem && section.treeProvider && section.treeProvider.expand(treeItem, true);
            section.treeRef.current.scrollIntoView(index, { block: placement });
        }
        // Run done() after the next animation frame so onScroll runs before we clean up.
        requestAnimationFrame(() => {
            done && done();
        });
    }
    _findTreeItemByLineNumber(selectedLineNumber, lines) {
        if (lines) {
            for (const line of lines) {
                if (line.data.lineNumber === selectedLineNumber) {
                    return line;
                }
                const child = this._findTreeItemByLineNumber(selectedLineNumber, line.childItems);
                if (child) {
                    return child;
                }
            }
        }
        return undefined;
    }
    _findTreeItemAndLineIndex(selectedLineNumber, lines, parentIndex) {
        if (lines && lines.length > 0) {
            for (let i = 0; i < lines.length; i++) {
                let lineNumber = lines[i].data.lineNumber;
                if (lineNumber === selectedLineNumber) {
                    return [lines[i], parentIndex ? parentIndex + i : i];
                }
                else if (!lines[i + 1] || lines[i + 1].data.lineNumber > selectedLineNumber) {
                    return this._findTreeItemAndLineIndex(selectedLineNumber, lines[i].childItems, i);
                }
            }
        }
        return [undefined, selectedLineNumber];
    }
    _recountLineIndexIfHasGroups(index, content) {
        var lines = content.split("\r\n");
        for (let i = 0; i < lines.length && index > i; i++) {
            if (lines[i] == "##[endgroup]") {
                index--;
            }
        }
        return index;
    }
    _getFindProps() {
        let lines = [];
        let sectionMarkers = [];
        let sectionLimits = [];
        let sectionKeys = [];
        Object.keys(this._sections).forEach((sKey) => {
            // get only expanded sections information
            const sectionData = this._showLog[sKey] && this._showLog[sKey].value && this._data[sKey] && this._data[sKey].value;
            if (sectionData) {
                sectionKeys.push(sKey);
                const listStats = sectionData.treeRef && sectionData.treeRef.current && sectionData.treeRef.current.getStats();
                listStats &&
                    sectionLimits.push({
                        startIndex: listStats.firstMaterialized,
                        endIndex: listStats.lastMaterialized
                    });
                const markerStart = Math.max(lines.length - 1, 0);
                lines = lines.concat(sectionData.lines);
                const markerEnd = Math.max(lines.length - 1, 0);
                sectionMarkers.push({
                    startIndex: markerStart,
                    endIndex: markerEnd
                });
            }
        });
        return {
            getContent: this._getFindContent,
            lines: lines,
            sectionKeys: sectionKeys,
            sectionMarkers: sectionMarkers,
            sectionLimits: sectionLimits
        };
    }
    _onToggleLog(key) {
        this._showLog[key].value = !this._showLog[key].value;
        if (this._showLog[key].value) {
            this._setContent(key);
            requestAnimationFrame(() => {
                this._scroll(this._data[key].value, 0, "nearest");
            });
        }
        let largestWidth = this._viewerWidth.value - 20;
        this.props.sections.value.forEach((section) => {
            let sectionData = this._data[section.key].value;
            if (this._showLog[section.key].value && !!sectionData.largestRowWidth)
                largestWidth = Math.max(largestWidth, sectionData.largestRowWidth);
        });
        this._largestRowWidth.value = largestWidth;
        this._reFind();
    }
    /**
     * Makes sure that section is expanded and fetches the content based on props, once fetched, this makes no attempt to get new content again unless 'force' is sent
     * @param key section to expand/show content
     * @param force If true, fetches content from props everytime, defaults to false
     */
    _setContent(key, force = false) {
        const section = this.props.sections.value.find((x) => x.key === key);
        if (section) {
            if (!this._showLog[key].value) {
                this._showLog[key].value = true;
            }
            const sectionData = this._data[key] && this._data[key].value;
            if (force || !sectionData.content) {
                this._pending[section.key] = true;
                const data = section.getData();
                if (data) {
                    if (data.then) {
                        data.then((data) => {
                            this._onData(section, key, data, force);
                        });
                    }
                    else {
                        this._onData(section, key, data, force);
                    }
                }
            }
            else {
                this._onData(section, key, sectionData.content);
            }
        }
    }
    _onData(section, key, data, force) {
        if (!this._data[key]) {
            console.error("section data can't be null", key);
            return;
        }
        const oldData = this._data[key].value;
        let oldLinesLength = 0;
        let content = data || "";
        const parser = section.parser || this._parser;
        let parseAllLines = oldData.showTimestamps != this.props.showTimestamps;
        if (!parseAllLines && content.indexOf(oldData.content) > 0) {
            parseAllLines = true;
        }
        // replace any timestamps in the beginning of the logs
        content = this.props.showTimestamps ? content : content.replace(TimestampRegex, "");
        // curled double quotes are left after echo command, so it lead to errors of parsing
        content = content.replace(/[“”]/g, '');
        let lines;
        if (!parseAllLines) {
            const startParseIndex = oldData.content ? oldData.content.length : 0;
            oldLinesLength = oldData.lines ? oldData.lines.length : 0;
            lines = parser.parse(content, startParseIndex, oldLinesLength);
        }
        else {
            lines = parser.parse(content);
        }
        let largestRowWidth = 0;
        lines.forEach((line) => {
            const nodesCount = line.nodes.length;
            if (nodesCount > 0) {
                const start = line.nodes[0].start;
                const end = line.nodes[nodesCount - 1].end;
                largestRowWidth = Math.max(largestRowWidth, (end - start) * 8 /* 8 is an estimate px width per character */);
            }
        });
        const newTreeItems = this._getTreeItemsFromLines(lines);
        const treeItems = oldData.treeItems && !parseAllLines ? oldData.treeItems.concat(newTreeItems) : newTreeItems;
        const treeItemProvider = new TreeItemProvider(treeItems);
        const asyncInfo = section.asyncInfo;
        if (asyncInfo) {
            if (asyncInfo.hasMore) {
                // if we have more data, inject the shimmer(by making an item as undefined) to the top of the log
                treeItemProvider.value.unshift({
                    underlyingItem: {
                        data: undefined
                    }
                });
            }
        }
        // adjust lize size to fill the view
        const treeRef = this._getTreeRef(key);
        if (this._largestRowWidth.value < largestRowWidth) {
            this._largestRowWidth.value = largestRowWidth;
        }
        let newData = {
            lines: oldLinesLength ? oldData.lines.concat(lines) : lines,
            content: content,
            findResult: oldData.findResult,
            treeProvider: treeItemProvider,
            treeItems: treeItems,
            treeRef: treeRef,
            largestRowWidth: largestRowWidth,
            showTimestamps: !!this.props.showTimestamps
        };
        // update observable
        this._data[key].value = newData;
        !force && this._reFind();
        delete this._pending[section.key];
        if (force && !section.noAutoScroll && this._autoScroll && oldData && oldData.lines && oldData.lines.length < newData.lines.length) {
            this._autoScrollLine = {
                key: key,
                line: newData.lines.length
            };
            this._scrollPending = true;
        }
        else {
            delete this._autoScrollLine;
        }
        this._scrollToLine();
    }
    _deferScroll() {
        // This is not a hack, by pushing the scroll to end, we are making sure that the render loop from observable completes and the element is available for us to scroll happily
        requestAnimationFrame(() => {
            this._scrollToLine();
        });
    }
    _scrollToLine() {
        let lineToScroll = this._toLine || this._findLine.value || this._autoScrollLine;
        if (!lineToScroll || this._pending[lineToScroll.key]) {
            // If the content is still pending no-op
            return;
        }
        const sectionData = lineToScroll && this._data[lineToScroll.key] && this._data[lineToScroll.key].value;
        if (this._scrollPending && lineToScroll && sectionData && sectionData.lines && this._container.current && sectionData.lines.length > 0) {
            // get our section element first
            const section = this._container.current.querySelector(`[${dataSection}="${lineToScroll.key}"]`);
            if (section) {
                const isSnap = section.querySelector('[data-snap="1"]');
                if (isSnap) {
                    // if this is a snap element, defer
                    this._deferScroll();
                    return;
                }
                const cleanUp = () => {
                    this._scrollPending = false;
                    // clean up the temporary lines to scroll
                    if (lineToScroll === this._toLine) {
                        this._toLine = undefined;
                    }
                    if (lineToScroll === this._findLine.value) {
                        this._findLine.value = undefined;
                    }
                };
                let placement = "nearest";
                if (lineToScroll === this._toLine || lineToScroll === this._findLine.value) {
                    // when asked to scroll to a particular line explictly (through URL), place it in center
                    placement = "center";
                }
                if (lineToScroll === this._autoScrollLine) {
                    placement = "end";
                }
                // Use the legacy index recount only when neither grouped-log scroll fix is enabled.
                if (!this.props.scrollToLogLineWhenGroupLogsArePresent && !this.props.isGroupedLogTreeScrollIndexFixEnabled) {
                    lineToScroll.line = this._recountLineIndexIfHasGroups(lineToScroll.line, sectionData.content);
                }
                sectionData && this._scroll(sectionData, lineToScroll.line, placement, cleanUp);
            }
        }
    }
}
function SecStatus(props) {
    const [spin, setSpin] = React.useState(false);
    React.useLayoutEffect(() => {
        if (props.spinner) {
            const timerId = setTimeout(() => {
                props.spinner && setSpin(true);
            }, 150);
            return () => {
                clearTimeout(timerId);
            };
        }
        else {
            setSpin(false);
        }
    }, [props.spinner]);
    return (React.createElement(React.Fragment, null,
        spin && React.createElement(Spinner, { size: SpinnerSize.xSmall, className: "status-spinner" }),
        !spin && (React.createElement(React.Fragment, null,
            props.statusProps && (React.createElement(Status, Object.assign({}, props.statusProps, { className: `flex-self-center fontSizeM ${props.noStatusFaint ? "" : "faint"}`, size: StatusSize.m }))),
            props.iconProps && React.createElement(Icon, Object.assign({}, props.iconProps, { className: "fontSizeML status-icon" }))))));
}
class Find {
    constructor() {
        this._toScrollIndex = 0;
        this._value = "";
        this._result = new ObservableValue(undefined);
        this._onChange = (event, value, props) => {
            this._value = value;
            // we need to reset the index to scroll if the search text or sections to search in are changed
            if (this._value !== this._prevValue || props.sectionKeys !== this._prevSearchSectionKeys) {
                this._toScrollIndex = 0;
            }
            this._prevValue = value;
            this._prevSearchSectionKeys = props.sectionKeys;
            let result;
            let findResultLines = [];
            let markers = {};
            if (value) {
                const lines = props.lines;
                // find the actual starting point
                for (let markerIndex = 0; markerIndex < props.sectionMarkers.length; markerIndex++) {
                    const markerStart = Math.max(findResultLines.length - 1, -1);
                    const sectionKey = props.sectionKeys[markerIndex];
                    // lines: ...|sectionStart.....pageStart......sectionEnd|...
                    const sectionStart = props.sectionMarkers[markerIndex].startIndex;
                    const pageStart = Math.max(props.sectionLimits[markerIndex].startIndex, 0);
                    const sectionEnd = props.sectionMarkers[markerIndex].endIndex;
                    // we should jump start to the line for this section, to start our search from the line user sees
                    // lines: ...|sectionStart.....[pageStart......sectionEnd]|...
                    // pageStart to end loop
                    for (let index = sectionStart + pageStart; index <= sectionEnd; index++) {
                        const line = lines[index];
                        const lineNumber = markerIndex > 0 ? Math.max(index - sectionStart, 0) : index + 1;
                        this._match(value, sectionKey, line, lineNumber, findResultLines, props);
                    }
                    // lines: ...|[sectionStart.....]pageStart......sectionEnd|...
                    // start to pageStart loop
                    if (pageStart !== sectionStart) {
                        for (let index = sectionStart; index <= pageStart; index++) {
                            const line = lines[index];
                            const lineNumber = markerIndex > 0 ? Math.max(index - sectionStart, 0) : index + 1;
                            this._match(value, sectionKey, line, lineNumber, findResultLines, props);
                        }
                    }
                    const markerEnd = Math.max(findResultLines.length - 1, 0);
                    if (findResultLines.length > 0) {
                        markers[sectionKey] = {
                            startIndex: markerStart + 1,
                            endIndex: markerEnd
                        };
                    }
                }
            }
            if (findResultLines.length > 0) {
                result = {
                    lines: findResultLines,
                    text: value,
                    toScrollIndex: this._toScrollIndex,
                    scrollCount: findResultLines.length,
                    markers: markers
                };
            }
            this._result.value = result;
        };
    }
    find(text, props) {
        this._onChange(null, text, props);
    }
    get result() {
        return this._result;
    }
    down() {
        if (this._result.value) {
            const toChange = this._toScrollIndex + 1;
            this._toScrollIndex = toChange > this._result.value.lines.length - 1 ? 0 : toChange;
            var newResult = Object.assign({}, this._result.value);
            newResult.toScrollIndex = this._toScrollIndex;
            this._result.value = newResult;
        }
    }
    up() {
        if (this._result.value) {
            const toChange = this._toScrollIndex - 1;
            this._toScrollIndex = toChange < 0 ? this._result.value.lines.length - 1 : toChange;
            var newResult = Object.assign({}, this._result.value);
            newResult.toScrollIndex = this._toScrollIndex;
            this._result.value = newResult;
        }
    }
    _match(value, sectionKey, line, lineNumber, result, props) {
        let foundMatch = false;
        const nodesCount = line.nodes.length;
        if (nodesCount > 0) {
            const start = line.nodes[0].start;
            const end = line.nodes[nodesCount - 1].end;
            const content = props.getContent(sectionKey);
            const data = getText(content.substring(start, end));
            if (data.indexOf(getText(value)) !== -1) {
                foundMatch = true;
            }
        }
        if (foundMatch) {
            result.push({
                key: sectionKey,
                line: lineNumber
            });
        }
    }
}
/* tslint:enable*/ 
